import { IContractType } from "store/queries/contractFeeSchedules";
import {
  DraftSiteTier,
  ICreateDraftRunRequest,
} from "store/queries/contractModelDrafts";

/** The modal's contract-type answer: a type, "not sure" (the AI decides), or not answered yet. */
export type INewRunTypeChoice = IContractType | "unsure" | null;

/** The ONE contract type whose provider bills from an office vs facility rate column. */
export const needsSiteTier = (choice: INewRunTypeChoice): boolean =>
  choice === "Professional";

/** CreateRunDto caps the name at 200 characters. */
export const RUN_NAME_MAX = 200;

/** A run's name, built from what she picked — there is no Name field any more. */
export const buildRunName = (
  providerName: string | null | undefined,
  payerName: string | null | undefined
): string =>
  [providerName?.trim(), payerName?.trim()]
    .filter(Boolean)
    .join(" — ")
    .slice(0, RUN_NAME_MAX);

export interface INewRunForm {
  providerId: string | null;
  payerId: number | null;
  typeChoice: INewRunTypeChoice;
  siteTier: DraftSiteTier | null;
}

/** Provider, payer and a contract-type answer are required; the rate tier only for Professional. */
export const canCreateNewRun = (form: INewRunForm): boolean =>
  !!form.providerId &&
  form.payerId != null &&
  form.typeChoice !== null &&
  (!needsSiteTier(form.typeChoice) || !!form.siteTier);

/**
 * "Start modeling": the fields above, and at least one document — the model is created, the files go up
 * and generation starts in one step, so there is nothing to start without a document.
 */
export const canStartModeling = (
  form: INewRunForm,
  files: readonly unknown[]
): boolean => canCreateNewRun(form) && files.length > 0;

/**
 * The standalone "Start modeling" request. Every new run starts with the facts pass (Ani's flow), so `stage`
 * is sent explicitly — the server's own default for a run with no contract is `full`. The rate tier
 * is sent only for a Professional contract; "not sure" is `contractType: null`.
 */
export const buildStandaloneRunRequest = (
  form: INewRunForm,
  name: string
): ICreateDraftRunRequest => ({
  providerId: form.providerId ?? undefined,
  payerId: form.payerId ?? undefined,
  contractType: form.typeChoice === "unsure" ? null : form.typeChoice,
  ...(needsSiteTier(form.typeChoice) && form.siteTier
    ? { siteTier: form.siteTier }
    : {}),
  stage: "facts",
  name: name || undefined,
});

export interface IExistingContractCandidate {
  id: string;
  providerId: string;
  payerId: number;
  status?: string;
  effectiveDate?: string | null;
  contractType?: IContractType | null;
  name?: string;
}

const STATUS_RANK: Record<string, number> = { Active: 0, Draft: 1 };

/**
 * The contract to offer for this provider + payer, or null. Only a contract the provider OWNS counts:
 * the list endpoint also returns contracts the provider is merely linked to, and modeling one of those
 * would model its owner, not the provider she picked. Active before Draft before anything else, then
 * the newest effective date.
 */
export const pickExistingContract = <T extends IExistingContractCandidate>(
  contracts: T[] | undefined,
  providerId: string | null,
  payerId: number | null
): T | null => {
  if (!providerId || payerId == null || !contracts?.length) return null;
  const own = contracts.filter(
    (c) => c.providerId === providerId && c.payerId === payerId
  );
  if (!own.length) return null;
  const rank = (c: T) => STATUS_RANK[c.status ?? ""] ?? 2;
  return [...own].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (b.effectiveDate ?? "").localeCompare(a.effectiveDate ?? "")
  )[0];
};

/**
 * The type sent when she models the EXISTING contract: her answer when it is a type; otherwise the
 * contract's own type; "not sure" with no type on the contract is `null` (the AI decides). `undefined`
 * means she has not answered and the contract records no type — the button waits for an answer.
 */
export const existingContractType = (
  choice: INewRunTypeChoice,
  contract: { contractType?: IContractType | null }
): IContractType | null | undefined => {
  if (choice && choice !== "unsure") return choice;
  if (contract.contractType) return contract.contractType;
  return choice === "unsure" ? null : undefined;
};

/**
 * Whether files added for an EXISTING contract may start its model themselves (`extractWithAi` on the
 * upload). The upload starts the run with the CONTRACT's own type (null when it records none), so the flag
 * is used only when that is the type she would get from "Model that contract" anyway. Otherwise the files
 * go up plain and the run is created with her answer — same idempotency key, so it is the same run.
 */
export const uploadStartsExistingModel = (
  type: IContractType | null | undefined,
  contract: { contractType?: IContractType | null }
): boolean => type !== undefined && type === (contract.contractType ?? null);

/** How the existing contract is named in the hint: its name, then the effective date and status. */
export const existingContractLabel = (
  contract: IExistingContractCandidate,
  fallbackName: string
): string => {
  const date = contract.effectiveDate
    ? new Date(contract.effectiveDate).toLocaleDateString(undefined, {
        dateStyle: "medium",
        timeZone: "UTC",
      })
    : null;
  const details = [
    date ? `effective ${date}` : null,
    contract.status ? contract.status.toLowerCase() : null,
  ].filter(Boolean);
  const name = contract.name?.trim() || fallbackName;
  return details.length ? `${name} (${details.join(", ")})` : name;
};
